import React, { useState } from 'react';
import { Modal, Button } from 'antd';
import { useQuery } from '@apollo/client';
// Api
import { GET_DELIVERABLE_REVISION_LIMIT } from 'api/campaignV2/queries';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_deliverables,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import { GetDeliverableRevisionLimit } from 'api/campaignV2/types/GetDeliverableRevisionLimit';
import {
  CampaignOfferStatus,
  CBDeliverableStatus,
  CbDeliverableV2Status,
} from 'api/graphql-global-types';
// Constants
import {
  CampaignVersion,
  DEFAULT_DELIVERABLE_REVISION_LIMIT,
} from 'constants/global';
// Components
import CampaignDeliverablesSubmitForReviewForm from './CampaignDeliverablesSubmitForReviewForm/CampaignDeliverablesSubmitForReviewForm';
import CampaignDeliverablesSubmissionReview from './CampaignDeliverablesSubmissionReview/CampaignDeliverablesSubmissionReview';
import CampaignDeliverablesRevisionHistory from './CampaignDeliverablesRevisionHistory/CampaignDeliverablesRevisionHistory';
// Styles
import styles from './CampaignDeliverablesReview.module.scss';

type CampaignDeliverablesReviewProps = {
  isRevisionHistoryReadOnly: boolean;
  offer: GetCampaignOffers_getCampaignOffers_entities;
  onClose: () => void;
  onFinish: () => void;
  refetch?: () => void;
};

const CampaignDeliverablesReview = ({
  isRevisionHistoryReadOnly,
  offer,
  onClose,
  onFinish,
  refetch,
}: CampaignDeliverablesReviewProps): JSX.Element => {
  const [showHistory, setShowHistory] = useState<boolean>(
    isRevisionHistoryReadOnly
  );

  const { data } = useQuery<GetDeliverableRevisionLimit>(
    GET_DELIVERABLE_REVISION_LIMIT
  );

  const deliverableRevisionLimit =
    data?.getDeliverableRevisionLimit || DEFAULT_DELIVERABLE_REVISION_LIMIT;

  const isV3Campaign = offer.campaign.v === CampaignVersion.V3;

  const deliverables = isV3Campaign ? offer.deliverablesV2 : offer.deliverables;

  const reviewDeliverable:
    | GetCampaignOffers_getCampaignOffers_entities_deliverables
    | GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
    | undefined = isV3Campaign
    ? undefined // Don't show review for V3 campaigns - handled by TimelineDeliverablesReviewModal
    : (
        deliverables as GetCampaignOffers_getCampaignOffers_entities_deliverables[]
      )?.find((item) => item.status === CBDeliverableStatus.ON_REVIEW);

  const isLimitReached = isV3Campaign
    ? offer.deliverablesV2?.length === deliverableRevisionLimit + 1
    : offer.deliverables?.length === deliverableRevisionLimit + 1;

  const showNewDeliverableForReviewForm: boolean =
    offer.statusV2 === CampaignOfferStatus.ACCEPTED ||
    (offer.statusV2 === CampaignOfferStatus.DELIVERABLES_REJECTED &&
      !isLimitReached) ||
    (isV3Campaign &&
      (offer.deliverablesV2?.some(
        (deliverable) => deliverable.status !== CbDeliverableV2Status.PUBLISHED
      ) ??
        false));

  const hasDeliverables = !!deliverables?.length && deliverables.length > 0;

  const showToggleHistoryButton: boolean =
    hasDeliverables && !isRevisionHistoryReadOnly;

  const handleToggleHistory = () => {
    setShowHistory((prevState) => !prevState);
  };

  const handleClose = () => {
    onClose();
    setShowHistory(false);
  };

  const isV2Campaign = offer.campaign.v === CampaignVersion.V2;

  return (
    <Modal
      title={
        isRevisionHistoryReadOnly ? 'Revision History' : 'Upload Deliverables'
      }
      visible={!!offer}
      footer={null}
      onCancel={handleClose}
      width="850px"
    >
      {showToggleHistoryButton && (
        <div className={styles.buttonWrapper}>
          <Button onClick={handleToggleHistory}>
            {offer && showHistory ? 'Hide history' : 'Show history'}
          </Button>
        </div>
      )}
      {deliverables?.length && showHistory ? (
        <CampaignDeliverablesRevisionHistory
          deliverables={deliverables}
          isV2Campaign={isV2Campaign}
          isV3Campaign={isV3Campaign}
        />
      ) : null}

      {reviewDeliverable && (
        <CampaignDeliverablesSubmissionReview
          deliverable={reviewDeliverable}
          offer={offer}
          offerId={offer.id || ''}
          ownerId={offer.campaign.agency.id || ''}
          isV2Campaign={isV2Campaign}
          isV3Campaign={isV3Campaign}
          onFinish={onFinish}
        />
      )}
      {showNewDeliverableForReviewForm && (
        <CampaignDeliverablesSubmitForReviewForm
          offer={offer}
          isV2Campaign={isV2Campaign}
          isV3Campaign={isV3Campaign}
          onFinish={onFinish}
          refetch={refetch}
        />
      )}
    </Modal>
  );
};

export default CampaignDeliverablesReview;
